import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, FileText, Mic, Copy, Check, Trash2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

const ContentHistory = ({ contents, onClose, onDelete }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = async (content) => {
    const fullContent = `${content.title}\n\n${content.content}\n\nViral Tip: ${content.viral_tip}`;
    await navigator.clipboard.writeText(fullContent);
    setCopiedId(content.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    return type === 'script' ? Mic : FileText;
  };

  const getAudienceColor = (audience) => {
    switch(audience) {
      case 'patients': return 'bg-[#CCFF00] text-black';
      case 'sponsors': return 'bg-[#7000FF] text-white';
      case 'physicians': return 'bg-[#0066FF] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <motion.div
      className="fixed right-6 top-36 z-50 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      data-testid="content-history-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#7000FF] to-[#0066FF] text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5" />
            <div>
              <h3 className="font-heading font-bold text-sm">MY CONTENT</h3>
              <p className="text-xs text-white/70">{contents.length} saved items</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="history-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content List */}
      <ScrollArea className="h-[500px]">
        <div className="p-4 space-y-3">
          {contents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No content yet</p>
              <p className="text-gray-400 text-sm mt-1">
                Generated content will appear here
              </p>
            </div>
          ) : (
            contents.map((content) => {
              const TypeIcon = getTypeIcon(content.format_type);
              const isExpanded = expandedId === content.id;
              
              return (
                <motion.div
                  key={content.id}
                  className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Header */}
                  <div 
                    className="p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : content.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getAudienceColor(content.audience)}`}>
                            {content.audience}
                          </span>
                          <span className="text-[10px] text-gray-400 uppercase">
                            {content.format_type}
                          </span>
                        </div>
                        <h4 className="font-medium text-sm text-gray-900 line-clamp-2">
                          {content.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(content.created_at)}
                        </div>
                      </div>
                      <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <motion.div
                      className="border-t border-gray-200"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <div className="p-3">
                        {/* Context tags */}
                        {(content.therapeutic_area || content.disease) && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {content.therapeutic_area && (
                              <span className="text-[10px] px-2 py-0.5 bg-gray-200 rounded-full">
                                {content.therapeutic_area}
                              </span>
                            )}
                            {content.disease && (
                              <span className="text-[10px] px-2 py-0.5 bg-gray-200 rounded-full">
                                {content.disease}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Preview */}
                        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-3">
                          <p className="text-xs text-gray-700 line-clamp-6 whitespace-pre-wrap">
                            {content.content}
                          </p>
                        </div>

                        {/* Viral tip */}
                        {content.viral_tip && (
                          <div className="bg-[#CCFF00]/20 rounded-lg p-2 mb-3">
                            <p className="text-[10px] font-bold text-gray-700">VIRAL TIP</p>
                            <p className="text-xs text-gray-600 mt-1">{content.viral_tip}</p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleCopy(content)}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-black text-white rounded-lg text-xs font-medium hover:bg-[#7000FF] transition-colors"
                          >
                            {copiedId === content.id ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                Copy
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => onDelete(content.id)}
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`delete-content-${content.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </motion.div>
  );
};

export default ContentHistory;
